// Katalog des Preismodells (gültig ab September 2026). Quelle ist die
// Landingpage (aivy-dashboard-landingpage, origin/production) mit
// src/components/pricing/pricingModel.js und lp.pricing.matrix. Der Abgleich
// läuft über UPSTREAM.json und lp-parity.test.js.
//
// Bewusst ohne Imports. Aivy OS kopiert diese Datei byte-gleich nach
// src/constants/pricing-catalog.js. Die SHA-256 des Dateiinhalts steht in
// UPSTREAM.json unter downstream.plansSha256, plans.test.js prüft sie hier,
// der Checksummen-Test im OS prüft dieselbe Zahl. Änderungen also immer hier
// machen, danach die Datei ins OS kopieren und die Checksumme in beiden Repos
// nachziehen.

// Paket-IDs wie auf der LP. custom ist ein Individualvertrag ohne Katalogwerte.
export const PLAN_ORDER = ['start', 'grow', 'scale', 'unlimited']
export const CUSTOM_PLAN = 'custom'

// Einziger Sentinel für unbegrenzt. Gilt nur bei Plan-Payments, ab diesem Wert
// ist eine Menge unbegrenzt. Legacy und Trials laufen mit Rohwerten wie heute.
// Nie null speichern, denn n >= null ist in JS true und sperrt das heutige
// Seat-Gate.
export const UNLIMITED = 999999

// Jede Zusatzstelle vergrößert den Talentspeicher (LP SPEICHER_JE_ZUSATZSTELLE)
export const STORAGE_PER_ADDON_CAREER = 50

// Ab diesem Anteil zeigt ein Meter gelb und der Usage-Alert greift
export const WARNING_RATIO = 0.8

// Mengen und das Feld am Payment, in dem sie stehen. stripehook belegt die
// Felder schon heute (active_job_listings, dashboard_users_seats,
// active_talent_storage_credits).
export const RESOURCES = {
  careers: { field: 'career_count' },
  seats: { field: 'seat_count' },
  talentStorage: { field: 'spaces_count' }
}

// Infinity steht nur im Katalog und in aufgelösten Limits. Gespeichert wird
// über toStoredCount, das Infinity auf UNLIMITED abbildet. Preise erscheinen im
// Dashboard nicht, addonPrice dient Aivy OS und dem LP-Abgleich. Unlimited
// kennt keine Zusatzstellen und Zusatzzugänge, deshalb null.
export const PLANS = {
  start: {
    id: 'start',
    name: 'Start',
    order: 0,
    limits: { careers: 5, seats: 3, talentStorage: 250 },
    max: { careers: 12, seats: 5 },
    addonPrice: { career: 119, seat: 69 }
  },
  grow: {
    id: 'grow',
    name: 'Growth',
    order: 1,
    limits: { careers: 20, seats: 10, talentStorage: 2000 },
    max: { careers: 38, seats: Infinity },
    addonPrice: { career: 149, seat: 69 }
  },
  scale: {
    id: 'scale',
    name: 'Scale',
    order: 2,
    limits: { careers: 50, seats: 25, talentStorage: 5000 },
    max: { careers: 68, seats: Infinity },
    addonPrice: { career: 199, seat: 69 }
  },
  unlimited: {
    id: 'unlimited',
    name: 'Unlimited',
    order: 3,
    limits: { careers: Infinity, seats: Infinity, talentStorage: Infinity },
    max: { careers: Infinity, seats: Infinity },
    addonPrice: { career: null, seat: null }
  }
}

export const AVAILABILITY = { YES: 'yes', ADDON: 'addon', NO: 'no' }

const availability = (start, grow, scale, unlimited) => ({
  start,
  grow,
  scale,
  unlimited
})

// Verfügbarkeit je PRODUCTS-Wert und Paket. yes heißt im Paket enthalten,
// addon zubuchbar, no nicht buchbar. Unlimited enthält alles.
// SMARTPREDICT ist die datengetriebene Anforderungsanalyse, BROWSERPLUGIN die
// ATS-Anbindung über Plugin. CUSTOM_REPORTS, LANGUAGE_FR und LANGUAGE_NL sind
// neue Enum-Werte, die erst mit dem Schema-Deploy existieren.
export const PRODUCT_AVAILABILITY = {
  TEAM_ANALYSIS: availability('yes', 'yes', 'yes', 'yes'),
  BRANDING_BASIC: availability('yes', 'yes', 'yes', 'yes'),
  BRANDING: availability('no', 'yes', 'yes', 'yes'),
  BROWSERPLUGIN: availability('addon', 'yes', 'yes', 'yes'),
  SUB_COMPANIES: availability('no', 'yes', 'yes', 'yes'),
  SMARTPREDICT: availability('no', 'yes', 'yes', 'yes'),
  CROSSCOMPARISON: availability('no', 'yes', 'yes', 'yes'),
  ATTRACT: availability('no', 'yes', 'yes', 'yes'),
  INTERVIEWPAPER: availability('no', 'yes', 'yes', 'yes'),
  INTEGRATIONS: availability('no', 'yes', 'yes', 'yes'),
  REDIRECTTALENTURL: availability('no', 'yes', 'yes', 'yes'),
  PERMISSION_SYSTEM: availability('no', 'yes', 'yes', 'yes'),
  AUTOMATIONS: availability('no', 'yes', 'yes', 'yes'),
  API: availability('no', 'addon', 'addon', 'yes'),
  CUSTOM_REPORTS: availability('no', 'no', 'yes', 'yes'),
  LANGUAGE_FR: availability('no', 'addon', 'yes', 'yes'),
  LANGUAGE_NL: availability('no', 'addon', 'yes', 'yes'),
  TALENTPOOL: availability('yes', 'yes', 'yes', 'yes'),
  PUBLICINVITATIONLINK: availability('yes', 'yes', 'yes', 'yes')
}

// Produkte, die ein Paket-Preset setzt oder entfernt
export const MANAGED_PRODUCTS = Object.keys(PRODUCT_AVAILABILITY)

// Produkte außerhalb des Preismodells. Ein Preset lässt sie unverändert stehen.
export const UNMANAGED_PRODUCTS = [
  'SKILLCHECK',
  'CUSTOMIZE_UI',
  'FAKINGMODULE',
  'TEAM_ANALYSIS_BASIC'
]

const isPlanId = (planId) => PLAN_ORDER.includes(planId)

// Alle Produkte, die im Paket enthalten sind
export const presetProducts = (planId) =>
  isPlanId(planId)
    ? MANAGED_PRODUCTS.filter(
        (product) => PRODUCT_AVAILABILITY[product][planId] === AVAILABILITY.YES
      )
    : []

// Ersetzt nur verwaltete Produkte. FAKINGMODULE, SKILLCHECK, CUSTOMIZE_UI und
// TEAM_ANALYSIS_BASIC bleiben stehen, auch wenn das OS sie nicht anzeigt.
export const mergePresetProducts = (currentProducts, planId) => {
  const kept = (currentProducts || []).filter(
    (product) => !MANAGED_PRODUCTS.includes(product)
  )
  return [...new Set([...kept, ...presetProducts(planId)])]
}

// Gesperrte Funktionen mit stabilen Keys, entkoppelt vom PRODUCTS-Enum.
// products ist das Gate als Oder-Verknüpfung, ein gebuchtes Produkt reicht.
// catalogProducts sind die Produkte, die die LP für das Feature verkauft. Aus
// ihnen kommen Verfügbarkeit, Badge und Upgrade-Hinweis. Ohne das Feld gilt
// products.
// CUSTOM_API ist frei bei API, INTEGRATIONS oder BROWSERPLUGIN, weil derselbe
// Key unter #ats auch die atsGateway-Anbindungen authentifiziert (softgarden,
// dvinci, Teamtailor, SmartRecruiters). Sonst könnte ein Growth-Kunde seine
// inkludierte ATS-Integration nicht anbinden. Verkauft wird aber nur API, so
// wie die LP-Zeile Custom API. Deshalb steht CUSTOM_API im Katalog als Zusatz
// in Growth und Scale, obwohl das Gate dort über INTEGRATIONS frei ist.
// legacyKey ist der Boolean, unter dem PaymentContext das Gate heute liefert.
export const FEATURES = {
  ORIENTATION_MODULE: {
    key: 'ORIENTATION_MODULE',
    products: ['ATTRACT'],
    i18nKey: 'pricing.features.orientation_module',
    legacyKey: 'attract'
  },
  REDIRECT_AFTER_ASSESSMENT: {
    key: 'REDIRECT_AFTER_ASSESSMENT',
    products: ['REDIRECTTALENTURL'],
    i18nKey: 'pricing.features.redirect_after_assessment',
    legacyKey: 'redirectTalentUrl'
  },
  CROSS_MATCHING: {
    key: 'CROSS_MATCHING',
    products: ['CROSSCOMPARISON'],
    i18nKey: 'pricing.features.cross_matching',
    legacyKey: 'crossComparison'
  },
  PARTNER_ATS_INTEGRATION: {
    key: 'PARTNER_ATS_INTEGRATION',
    products: ['INTEGRATIONS'],
    i18nKey: 'pricing.features.partner_ats_integration',
    legacyKey: 'integrations'
  },
  GDPR_AUTOMATION: {
    key: 'GDPR_AUTOMATION',
    products: ['AUTOMATIONS'],
    i18nKey: 'pricing.features.gdpr_automation',
    legacyKey: 'automations'
  },
  PERMISSION_SYSTEM: {
    key: 'PERMISSION_SYSTEM',
    products: ['PERMISSION_SYSTEM'],
    i18nKey: 'pricing.features.permission_system',
    legacyKey: null
  },
  CUSTOM_API: {
    key: 'CUSTOM_API',
    products: ['API', 'INTEGRATIONS', 'BROWSERPLUGIN'],
    catalogProducts: ['API'],
    i18nKey: 'pricing.features.custom_api',
    legacyKey: null
  },
  CUSTOM_REPORTS: {
    key: 'CUSTOM_REPORTS',
    products: ['CUSTOM_REPORTS'],
    i18nKey: 'pricing.features.custom_reports',
    legacyKey: null
  },
  LANGUAGE_FR: {
    key: 'LANGUAGE_FR',
    products: ['LANGUAGE_FR'],
    i18nKey: 'pricing.features.language_fr',
    legacyKey: null
  },
  LANGUAGE_NL: {
    key: 'LANGUAGE_NL',
    products: ['LANGUAGE_NL'],
    i18nKey: 'pricing.features.language_nl',
    legacyKey: null
  }
}

export const FEATURE_KEYS = Object.keys(FEATURES)

// Die fünf Gates, die es schon vor dem Preismodell gab, mit ihrem Boolean im
// PaymentContext. In legacy und preview rechnen sie exakt wie heute.
export const LEGACY_FEATURE_KEYS = {
  ORIENTATION_MODULE: 'attract',
  REDIRECT_AFTER_ASSESSMENT: 'redirectTalentUrl',
  CROSS_MATCHING: 'crossComparison',
  PARTNER_ATS_INTEGRATION: 'integrations',
  GDPR_AUTOMATION: 'automations'
}

const AVAILABILITY_RANK = { no: 0, addon: 1, yes: 2 }

// Produkte, aus denen der Katalog die Verfügbarkeit liest. Das Gate prüft
// weiter products.
export const catalogProductsOf = (key) => {
  const feature = FEATURES[key]
  if (!feature) return []
  return feature.catalogProducts || feature.products
}

// Beste Verfügbarkeit über die Katalog-Produkte des Features, so wie die LP es
// verkauft. Unbekannte Keys und Pakete ergeben no.
export const featureAvailability = (key, planId) => {
  if (!FEATURES[key] || !isPlanId(planId)) return AVAILABILITY.NO

  return catalogProductsOf(key).reduce((best, product) => {
    const value = PRODUCT_AVAILABILITY[product]?.[planId] || AVAILABILITY.NO
    return AVAILABILITY_RANK[value] > AVAILABILITY_RANK[best] ? value : best
  }, AVAILABILITY.NO)
}

// Pakete, in denen das Feature enthalten ist
export const includedIn = (key) =>
  PLAN_ORDER.filter(
    (planId) => featureAvailability(key, planId) === AVAILABILITY.YES
  )

// Pakete, in denen das Feature zubuchbar ist
export const addonPlans = (key) =>
  PLAN_ORDER.filter(
    (planId) => featureAvailability(key, planId) === AVAILABILITY.ADDON
  )

// Kleinstes Paket, das das Feature enthält
export const minPlan = (key) => includedIn(key)[0] || null

// Entspricht LP speicher(). Jede Stelle über dem Paket bringt
// STORAGE_PER_ADDON_CAREER Talente dazu. Ohne Stellenzahl gilt das Paket.
export const deriveTalentStorage = (planId, careerCount) => {
  const plan = PLANS[planId]
  if (!plan) return null

  const base = plan.limits.talentStorage
  if (!Number.isFinite(base)) return Infinity

  const careers =
    typeof careerCount === 'number' && !Number.isNaN(careerCount)
      ? careerCount
      : plan.limits.careers

  return (
    base + Math.max(0, careers - plan.limits.careers) * STORAGE_PER_ADDON_CAREER
  )
}

// Gilt eine gespeicherte Menge als unbegrenzt
export const isUnlimitedCount = (n) =>
  n !== null && n !== undefined && n !== '' && Number(n) >= UNLIMITED

// Wandelt einen Katalog- oder Formularwert in den Wert für das Payment.
// Infinity und alles ab UNLIMITED wird UNLIMITED. null heißt ungültig und darf
// so nicht gespeichert werden.
export const toStoredCount = (value) => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  if (Number.isNaN(n) || n < 0) return null
  if (n >= UNLIMITED) return UNLIMITED
  return Math.floor(n)
}

const PLAN_ALIASES = {
  start: 'start',
  grow: 'grow',
  growth: 'grow',
  scale: 'scale',
  unlimited: 'unlimited',
  custom: CUSTOM_PLAN
}

// Identisch in Dashboard und OS. Akzeptiert Enum-Schreibweise (GROW) und
// LP-IDs (grow), dazu growth. Alles andere ergibt null und gilt als legacy.
export const normalizePlanId = (raw) => {
  if (typeof raw !== 'string') return null
  const key = raw.trim().toLowerCase()
  return Object.prototype.hasOwnProperty.call(PLAN_ALIASES, key)
    ? PLAN_ALIASES[key]
    : null
}
